import { useMemo, useState } from "react";
import {
  Link,
  useNavigate,
} from "react-router-dom";

import "../styles/runway-calculator.css";
import AppFooter from "../components/AppFooter";
import logo from "../assets/logo_basic.png";

import SEO from "../components/SEO";
import {
  financialRunwayApplicationSchema,
} from "../seo/schemas/softwareApplicationSchema";


// ============================================================
// IMPORTANT PRIVACY DESIGN
// ============================================================
//
// This calculator DOES NOT:
// - call saveFinancialReadiness()
// - call any financial save API
// - insert/update Supabase
// - use localStorage
// - use sessionStorage
//
// Results exist only in React state.
// Refreshing/leaving this page removes the result.
// ============================================================


// ============================================================
// REGIONAL RULES
// ============================================================

const REGIONAL_RULES = {
  USA: {
    region_group: "USA",
    inflation_drag_coefficient: 0.04,
    employment_type: "At-will (0 notice)",
    health_insurance: "COBRA costs apply",
    financial_focus: [
      "401k vesting",
      "RSU vesting",
    ],
    notice_period_months: 0,
  },

  UK: {
    region_group: "UK",
    inflation_drag_coefficient: 0.04,
    severance:
      "Statutory redundancy pay",
    savings_accounts: ["ISA"],
    notice_period_months: 3,
  },

  UAE: {
    region_group: "GCC",
    inflation_drag_coefficient: 0.04,
    benefits: [
      "End-of-Service Gratuity",
    ],
    visa_logic: {
      type: "Visa Runway",
      countdown_days: 30,
    },
    upfront_costs: [
      "Annual Rent",
      "School Fees",
    ],
  },

  "Saudi Arabia (KSA)": {
    region_group: "GCC",
    inflation_drag_coefficient: 0.04,
    benefits: [
      "End-of-Service Gratuity",
    ],
  },

  Qatar: {
    region_group: "GCC",
    inflation_drag_coefficient: 0.04,
    benefits: [
      "End-of-Service Gratuity",
    ],
  },

  Oman: {
    region_group: "GCC",
    inflation_drag_coefficient: 0.04,
    benefits: [
      "End-of-Service Gratuity",
    ],
  },

  Kuwait: {
    region_group: "GCC",
    inflation_drag_coefficient: 0.04,
    benefits: [
      "End-of-Service Gratuity",
    ],
  },

  Bahrain: {
    region_group: "GCC",
    inflation_drag_coefficient: 0.04,
    benefits: [
      "End-of-Service Gratuity",
    ],
  },

  India: {
    region_group: "India",
    inflation_drag_coefficient: 0.08,
    benefits: [
      "Provident Fund",
    ],
  },

  Pakistan: {
    region_group: "Pakistan",
    inflation_drag_coefficient: 0.15,
    expenses: [
      "Family Support/Black Tax",
    ],
  },
};


// ============================================================
// INITIAL FORM
// ============================================================

const initialForm = {
  residency: "Expatriate",

  months_until_runway: "",

  monthly_basic_salary: "",
  monthly_allowances: "",
  other_monthly_income: "",

  current_savings: "",

  years_service: "",
  provident_fund: "",

  rent_mortgage: "",
  loan_emis: "",
  utilities: "",
  groceries_transport: "",
  school_fees: "",

  total_debt: "",
  family_support: "",

  cobra_cost: "",
  cobra_enabled: false,

  visa_expiry: "",

  unemployment_benefit: "",

  rsu_vesting: "",
  k401_balance: "",

  isa_savings: "",

  age_years: "",

  india_emp_type: "Permanent",

  reason_for_leaving:
    "Termination",
};


// ============================================================
// HELPERS
// ============================================================

function num(value) {
  return Number(
    value || 0
  );
}


function money(value) {
  return Number(
    value || 0
  ).toLocaleString(
    "en-US",
    {
      maximumFractionDigits: 0,
    }
  );
}


// ============================================================
// INPUT FIELD
// ============================================================

function InputField({
  id,
  label,
  type = "number",
  value,
  onChange,
}) {
  return (
    <div className="rc-form-group">

      <label htmlFor={id}>
        {label}
      </label>

      <input
        id={id}
        name={id}
        type={type}
        value={value ?? ""}
        onChange={onChange}
        placeholder={
          type === "date"
            ? undefined
            : "0"
        }
      />

    </div>
  );
}


// ============================================================
// COMPONENT
// ============================================================

export default function RunwayCalculator() {

  const navigate =
    useNavigate();


  // ==========================================================
  // STATE
  // ==========================================================

  const [
    region,
    setRegion,
  ] =
    useState("USA");


  const [
    form,
    setForm,
  ] =
    useState(initialForm);


  // ----------------------------------------------------------
  // This is the ONLY place the calculated result is stored.
  //
  // React memory only.
  //
  // Refresh / leave page = result disappears.
  // ----------------------------------------------------------

  const [
    result,
    setResult,
  ] =
    useState(null);


  // ==========================================================
  // CONFIGURATION
  // ==========================================================

  const config =
    REGIONAL_RULES[
      region
    ];


  const group =
    config?.region_group ||
    region;


  const isNational =
    form.residency ===
    "National";


  const isGCC =
    group ===
    "GCC";


  const isUSUK =
    group === "USA" ||
    group === "UK";


  const regions =
    useMemo(
      () =>
        Object.keys(
          REGIONAL_RULES
        ),
      []
    );


  // ==========================================================
  // FIELD UPDATE
  // ==========================================================

  const setVal = (
    key,
    value
  ) => {

    setForm(
      (
        previous
      ) => ({
        ...previous,

        [key]:
          value,
      })
    );

    // Existing displayed calculation is no longer
    // guaranteed to match new input.

    setResult(null);
  };


  // ==========================================================
  // RENDER INPUT
  // ==========================================================

  const renderInput = (
    id,
    label,
    type = "number"
  ) => (
    <InputField
      id={id}
      label={label}
      type={type}
      value={form[id]}
      onChange={(
        event
      ) =>
        setVal(
          id,
          event.target.value
        )
      }
    />
  );


  // ==========================================================
  // CALCULATE
  //
  // IMPORTANT:
  //
  // LOCAL / IN-MEMORY CALCULATION ONLY.
  //
  // There is intentionally:
  //
  // NO saveFinancialReadiness()
  // NO API request
  // NO Supabase insert/update
  // NO localStorage
  // NO sessionStorage
  //
  // ==========================================================

  const calculate = () => {

    // ========================================================
    // BASIC INCOME VALUES
    // ========================================================

    const basicSalary =
      num(
        form.monthly_basic_salary
      );


    const allowances =
      num(
        form.monthly_allowances
      );


    const salary =
      basicSalary +
      allowances;


    const currentSavings =
      num(
        form.current_savings
      );


    const otherIncome =
      num(
        form.other_monthly_income
      );


    const monthsUntil =
      num(
        form.months_until_runway
      );


    // ========================================================
    // EXPENSES
    // ========================================================

    const rent =
      num(
        form.rent_mortgage
      );


    const loans =
      num(
        form.loan_emis
      );


    const utilities =
      num(
        form.utilities
      );


    const groceries =
      num(
        form.groceries_transport
      );


    const schoolFees =
      num(
        form.school_fees
      );


    const totalDebt =
      num(
        form.total_debt
      );


    // ========================================================
    // REGION-SPECIFIC VARIABLES
    // ========================================================

    let specificAssets =
      0;

    let specificBurn =
      0;

    let gratuity =
      0;

    let unemploymentBenefit =
      0;

    let alert =
      null;


    // ========================================================
    // USA
    // ========================================================

    if (
      group ===
      "USA"
    ) {

      specificAssets =
        (
          num(
            form.k401_balance
          ) *
          0.65
        ) +
        (
          num(
            form.rsu_vesting
          ) *
          0.7
        );


      if (
        form.cobra_enabled
      ) {

        specificBurn +=
          num(
            form.cobra_cost
          );
      }


      if (
        isNational
      ) {

        unemploymentBenefit =
          num(
            form.unemployment_benefit
          );
      }
    }


    // ========================================================
    // GCC
    // ========================================================

    if (
      group ===
      "GCC"
    ) {

      const years =
        num(
          form.years_service
        );


      if (
        !isNational
      ) {

        // ----------------------------------------------------
        // Saudi Arabia
        // ----------------------------------------------------

        if (
          region ===
          "Saudi Arabia (KSA)"
        ) {

          const first5 =
            Math.min(
              years,
              5
            );


          let fullPot =
            first5 *
            0.5 *
            salary;


          if (
            years >
            5
          ) {

            fullPot +=
              (
                years -
                5
              ) *
              salary;
          }


          let factor =
            1;


          if (
            form.reason_for_leaving ===
            "Resignation"
          ) {

            if (
              years <
              2
            ) {

              factor =
                0;

            }
            else if (
              years <
              5
            ) {

              factor =
                1 / 3;

            }
            else if (
              years <
              10
            ) {

              factor =
                2 / 3;
            }
          }


          gratuity =
            fullPot *
            factor;
        }


        // ----------------------------------------------------
        // Qatar
        // ----------------------------------------------------

        else if (
          region ===
          "Qatar"
        ) {

          gratuity =
            years >= 1
              ? (
                  basicSalary /
                  30
                ) *
                21 *
                years
              : 0;
        }


        // ----------------------------------------------------
        // Bahrain
        // ----------------------------------------------------

        else if (
          region ===
          "Bahrain"
        ) {

          const first3 =
            Math.min(
              years,
              3
            );


          gratuity =
            first3 *
            0.5 *
            basicSalary;


          if (
            years >
            3
          ) {

            gratuity +=
              (
                years -
                3
              ) *
              basicSalary;
          }
        }


        // ----------------------------------------------------
        // Oman
        // ----------------------------------------------------

        else if (
          region ===
          "Oman"
        ) {

          gratuity =
            salary *
            years;
        }


        // ----------------------------------------------------
        // UAE / Kuwait / General GCC
        // ----------------------------------------------------

        else {

          if (
            years >=
            1
          ) {

            const first5 =
              Math.min(
                years,
                5
              );


            let fullPot =
              (
                basicSalary /
                30
              ) *
              21 *
              first5;


            if (
              years >
              5
            ) {

              fullPot +=
                basicSalary *
                (
                  years -
                  5
                );
            }


            gratuity =
              Math.min(
                fullPot,

                basicSalary *
                24
              );
          }
        }
      }
    }


    // ========================================================
    // INDIA
    // ========================================================

    if (
      group ===
      "India"
    ) {

      specificBurn +=
        num(
          form.family_support
        );


      specificAssets =
        num(
          form.provident_fund
        ) *
        0.75;


      const years =
        num(
          form.years_service
        );


      const eligibility =
        form.india_emp_type ===
        "Fixed-Term"
          ? 1
          : 5;


      if (
        years >=
        eligibility
      ) {

        const gratuityBase =
          Math.max(
            basicSalary,

            salary *
              0.5
          );


        gratuity =
          Math.min(
            (
              gratuityBase *
              15 *
              years
            ) /
              26,

            2000000
          );
      }
    }


    // ========================================================
    // PAKISTAN
    // ========================================================

    if (
      group ===
      "Pakistan"
    ) {

      specificBurn +=
        num(
          form.family_support
        );


      specificAssets =
        num(
          form.provident_fund
        );


      const years =
        num(
          form.years_service
        );


      if (
        years >=
        1
      ) {

        gratuity =
          (
            salary /
            26
          ) *
          30 *
          years;
      }
    }


    // ========================================================
    // UK
    // ========================================================

    if (
      group ===
      "UK"
    ) {

      specificAssets =
        num(
          form.isa_savings
        );


      if (
        isNational
      ) {

        unemploymentBenefit =
          num(
            form.unemployment_benefit
          );
      }
    }


    // ========================================================
    // MONTHLY BURN
    // ========================================================

    const totalMonthlyBurn =
      rent +
      loans +
      utilities +
      groceries +
      schoolFees +
      specificBurn;


    let netBurn =
      totalMonthlyBurn -
      otherIncome -
      unemploymentBenefit;


    if (
      netBurn <
      0
    ) {

      netBurn =
        0;
    }


    // ========================================================
    // SURPLUS BEFORE TRANSITION
    // ========================================================

    const monthlySurplus =
      salary +
      otherIncome -
      totalMonthlyBurn;


    // ========================================================
    // FUTURE SAVINGS
    // ========================================================

    const futureSavings =
      monthlySurplus *
      monthsUntil;


    // ========================================================
    // TOTAL RUNWAY FUND
    // ========================================================

    const totalRunwayFund =
      currentSavings +
      specificAssets +
      gratuity +
      futureSavings -
      totalDebt;


    // ========================================================
    // INFLATION
    // ========================================================

    let inflation =
      config
        ?.inflation_drag_coefficient ||
      0.04;


    if (
      region ===
      "India"
    ) {

      inflation =
        0.04;
    }


    if (
      region ===
      "Pakistan"
    ) {

      inflation =
        0.07;
    }


    if (
      group ===
      "USA"
    ) {

      inflation =
        0.03;
    }


    // ========================================================
    // INFLATED MONTHLY BURN
    // ========================================================

    const inflatedBurn =
      netBurn *
      (
        1 +
        inflation
      );


    // ========================================================
    // FINANCIAL RUNWAY
    // ========================================================

    const financialRunway =
      inflatedBurn >
      0
        ? totalRunwayFund /
          inflatedBurn
        : 999;


    // ========================================================
    // DEBT WARNING
    // ========================================================

    if (
      totalDebt >
      totalRunwayFund +
        totalDebt *
          0.5
    ) {

      alert =
        "Debt risk: your debt burden is high compared with available runway funds.";
    }


    // ========================================================
    // SAFE VALUE
    // ========================================================

    const safeFinancialRunway =
      Number.isFinite(
        financialRunway
      )
        ? Math.max(
            0,
            Math.min(
              financialRunway,
              999
            )
          )
        : 999;


    // ========================================================
    // RESULT
    //
    // MEMORY ONLY
    // ========================================================

    const resultData = {

      totalRunwayFund,

      netBurn,

      gratuity,

      unemploymentBenefit,

      financialRunway:
        safeFinancialRunway,

      inflation,

      alert,
    };


    // ========================================================
    // DISPLAY RESULT
    //
    // This is the only "storage".
    //
    // React state disappears when page is closed/refreshed.
    // ========================================================

    setResult(
      resultData
    );
  };


  // ==========================================================
  // UI
  // ==========================================================

  return (

    <div
      className="
        app-shell
        bg-[#020617]
        text-slate-200
        antialiased
      "
    >

      <SEO
        title="Financial Runway Calculator"

        description="
          Estimate how long your savings,
          income and other financial resources
          may support your career transition.
        "

        path="/runway-calculator"

        image="/og-financial-runway.jpeg"

        type="website"

        author="Reignite Spirit"

        keywords="
          financial runway calculator,
          career transition calculator,
          savings runway,
          monthly burn rate,
          gratuity calculator,
          severance calculator
        "

        robots="
          index,
          follow,
          max-snippet:-1,
          max-image-preview:large,
          max-video-preview:-1
        "

        schema={
          financialRunwayApplicationSchema
        }
      />


      {/* =====================================================
          HEADER
      ===================================================== */}

      <header
        className="
          app-header
          bg-[#020617]/90
          backdrop-blur
          border-b
          border-white/10
          px-6
          py-4
        "
      >

        <div
          className="
            max-w-3xl
            mx-auto
            flex
            items-start
            justify-between
            gap-4
          "
        >

          <div>

            <button
              type="button"

              onClick={() =>
                navigate(-1)
              }

              className="
                text-sm
                text-slate-500
                hover:text-white
                inline-block
              "
            >
              ← Back
            </button>


            <h1
              className="
                text-3xl
                font-bold
                text-white
                mt-4
                mb-2
              "
            >
              Runway{" "}

              <span
                className="
                  text-[#eb6fbd]
                "
              >
                Calculator
              </span>
            </h1>


            {/* ===============================================
                PRIVACY MESSAGE
            =============================================== */}

            <p
              className="
                mt-2
                mb-3
                text-sm
                font-bold
                text-white
                leading-relaxed
              "
            >
              🔒 Private by design.
              Your financial inputs and
              calculated results are not
              stored.
            </p>


            <p
              className="
                mb-3
                text-xs
                text-slate-400
                leading-relaxed
              "
            >
              Your calculation exists only
              while this page is open.
              Refreshing or leaving the page
              removes the result.
            </p>


            <div
              className="
                h-1
                w-16
                bg-[#eb6fbd]
                rounded
              "
            />

          </div>


          <button
            type="button"

            onClick={() =>
              navigate(
                "/dashboard"
              )
            }

            className="
              shrink-0
              px-5
              py-2
              rounded-full
              bg-[#EB6AB5]
              text-[#0F0A24]
              font-semibold
              hover:bg-[#ff83d1]
              transition
              shadow-lg
            "
          >
            Dashboard
          </button>

        </div>

      </header>


      {/* =====================================================
          MAIN
      ===================================================== */}

      <main
        className="
          app-body
          px-6
          py-6
        "
      >

        <div
          className="
            max-w-3xl
            mx-auto
          "
        >

          <div
            className="
              rc-card
            "
          >

            <p
              className="
                rc-subtitle
              "
            >
              Select your region to configure
              financial parameters
            </p>


            {/* =================================================
                REGION
            ================================================= */}

            <div
              className="
                rc-form-group
              "
            >

              <label
                htmlFor="region"
              >
                Select Region
              </label>


              <select
                id="region"

                value={
                  region
                }

                onChange={(
                  event
                ) => {

                  setRegion(
                    event.target.value
                  );

                  setResult(
                    null
                  );
                }}
              >

                {regions.map(
                  (
                    regionName
                  ) => (

                    <option
                      key={
                        regionName
                      }

                      value={
                        regionName
                      }
                    >
                      {regionName}
                    </option>

                  )
                )}

              </select>

            </div>


            {/* =================================================
                RESIDENCY
            ================================================= */}

            <div
              className="
                rc-section
              "
            >

              <label>
                Residency Status
              </label>


              <div
                className="
                  rc-radio-row
                "
              >

                {[
                  "Expatriate",
                  "National",
                ].map(
                  (
                    status
                  ) => (

                    <label
                      key={
                        status
                      }

                      className="
                        rc-radio-pill
                      "
                    >

                      <input
                        type="radio"

                        name="residency"

                        value={
                          status
                        }

                        checked={
                          form.residency ===
                          status
                        }

                        onChange={() =>
                          setVal(
                            "residency",
                            status
                          )
                        }
                      />

                      {status}

                    </label>

                  )
                )}

              </div>

            </div>


            {/* =================================================
                1. EMPLOYMENT & INFLOWS
            ================================================= */}

            <h4
              className="
                rc-section-title
              "
            >
              1. Employment &amp; Inflows
            </h4>


            <div
              className="
                rc-section
              "
            >

              {renderInput(
                "months_until_runway",

                "Months until job loss/resignation"
              )}


              {renderInput(
                "monthly_basic_salary",

                "Monthly basic salary"
              )}


              {renderInput(
                "monthly_allowances",

                "Monthly fixed allowances"
              )}


              {renderInput(
                "other_monthly_income",

                "Other monthly income"
              )}


              {
                isNational &&
                isUSUK &&
                renderInput(
                  "unemployment_benefit",

                  "Expected unemployment benefit"
                )
              }

            </div>


            {/* =================================================
                2. RUNWAY FUNDS
            ================================================= */}

            <h4
              className="
                rc-section-title
              "
            >
              2. Runway Funds
            </h4>


            <div
              className="
                rc-section
              "
            >

              {renderInput(
                "current_savings",

                "Current cash savings"
              )}


              {/* USA */}

              {
                group ===
                  "USA" && (

                  <>

                    {renderInput(
                      "k401_balance",

                      "401k balance"
                    )}


                    {renderInput(
                      "rsu_vesting",

                      "RSU vesting amount"
                    )}

                  </>

                )
              }


              {/* GCC */}

              {
                isGCC && (

                  <>

                    {renderInput(
                      "years_service",

                      "Years of service"
                    )}


                    {
                      (
                        region ===
                          "Saudi Arabia (KSA)" ||

                        region ===
                          "Kuwait"
                      ) && (

                        <div
                          className="
                            rc-form-group
                          "
                        >

                          <label
                            htmlFor="
                              reason_for_leaving
                            "
                          >
                            Reason for leaving
                          </label>


                          <select
                            id="
                              reason_for_leaving
                            "

                            value={
                              form.reason_for_leaving
                            }

                            onChange={(
                              event
                            ) =>
                              setVal(
                                "reason_for_leaving",

                                event
                                  .target
                                  .value
                              )
                            }
                          >

                            <option
                              value="
                                Termination
                              "
                            >
                              Termination
                            </option>


                            <option
                              value="
                                End of Contract
                              "
                            >
                              End of Contract
                            </option>


                            <option
                              value="
                                Resignation
                              "
                            >
                              Resignation
                            </option>

                          </select>

                        </div>

                      )
                    }


                    {
                      !isNational &&
                      renderInput(
                        "visa_expiry",

                        "Visa expiry date",

                        "date"
                      )
                    }

                  </>

                )
              }


              {/* INDIA */}

              {
                group ===
                  "India" && (

                  <>

                    {renderInput(
                      "years_service",

                      "Total years of service"
                    )}


                    {renderInput(
                      "provident_fund",

                      "Provident fund balance"
                    )}


                    <div
                      className="
                        rc-form-group
                      "
                    >

                      <label
                        htmlFor="
                          india_emp_type
                        "
                      >
                        Employment type
                      </label>


                      <select
                        id="
                          india_emp_type
                        "

                        value={
                          form.india_emp_type
                        }

                        onChange={(
                          event
                        ) =>
                          setVal(
                            "india_emp_type",

                            event
                              .target
                              .value
                          )
                        }
                      >

                        <option
                          value="
                            Permanent
                          "
                        >
                          Permanent
                        </option>


                        <option
                          value="
                            Fixed-Term
                          "
                        >
                          Fixed-Term
                        </option>

                      </select>

                    </div>

                  </>

                )
              }


              {/* PAKISTAN */}

              {
                group ===
                  "Pakistan" && (

                  <>

                    {renderInput(
                      "years_service",

                      "Total years of service"
                    )}


                    {renderInput(
                      "provident_fund",

                      "Provident fund balance"
                    )}

                  </>

                )
              }


              {/* UK */}

              {
                group ===
                  "UK" && (

                  <>

                    {renderInput(
                      "age_years",
                      "Age"
                    )}


                    {renderInput(
                      "years_service",

                      "Years of service"
                    )}


                    {renderInput(
                      "isa_savings",

                      "ISA savings"
                    )}

                  </>

                )
              }

            </div>


            {/* =================================================
                3. MONTHLY EXPENSES
            ================================================= */}

            <h4
              className="
                rc-section-title
              "
            >
              3. Monthly Expenses
            </h4>


            <div
              className="
                rc-section
              "
            >

              {renderInput(
                "rent_mortgage",

                "Rent / mortgage"
              )}


              {renderInput(
                "loan_emis",

                "Loan EMIs / debt service"
              )}


              {renderInput(
                "utilities",

                "Utilities & subscriptions"
              )}


              {renderInput(
                "groceries_transport",

                "Groceries & transport"
              )}


              {renderInput(
                "school_fees",

                "Education / school fees"
              )}


              {renderInput(
                "total_debt",

                "Total outstanding debt"
              )}


              {/* USA COBRA */}

              {
                group ===
                  "USA" && (

                  <>
<label className="rc-check">
  <input
    type="checkbox"
    checked={form.cobra_enabled}
    onChange={(event) =>
      setVal(
        "cobra_enabled",
        event.target.checked
      )
    }
  />

  <span
    className="rc-checkbox-box"
    aria-hidden="true"
  />

  <span className="rc-check-text">
    Include COBRA health insurance cost?
  </span>
</label>
                    {
                      form
                        .cobra_enabled &&

                      renderInput(
                        "cobra_cost",

                        "Monthly COBRA cost"
                      )
                    }

                  </>

                )
              }


              {/* INDIA / PAKISTAN */}

              {
                (
                  group ===
                    "India" ||

                  group ===
                    "Pakistan"
                ) &&

                renderInput(
                  "family_support",

                  "Family support monthly expenses"
                )
              }

            </div>


            {/* =================================================
                CALCULATE BUTTON
            ================================================= */}

            <button
              type="button"

              className="
                rc-btn
              "

              onClick={
                calculate
              }
            >
              Calculate Runway
            </button>


            {/* =================================================
                PRIVACY CONFIRMATION
            ================================================= */}

            <div
              className="
                mt-4
                rounded-xl
                border
                border-emerald-400/20
                bg-emerald-400/5
                p-4
              "
            >

              <p
                className="
                  text-sm
                  font-bold
                  text-emerald-100
                "
              >
                🔒 Your financial information
                stays private.
              </p>


              <p
                className="
                  mt-1
                  text-xs
                  text-emerald-100/70
                  leading-relaxed
                "
              >
                Your entries and calculated
                result are not saved to your
                account or database. If you
                refresh or leave this page,
                the result will no longer be
                available.
              </p>

            </div>


            {/* =================================================
                DISCLAIMER
            ================================================= */}

            <div
              className="
                mt-5
                rounded-xl
                border
                border-amber-400/30
                bg-amber-400/5
                p-4
              "
            >

              <p
                className="
                  text-sm
                  text-amber-100
                  leading-relaxed
                "
              >
                Estimates are based on the
                information entered and the
                simplified regional assumptions
                currently implemented in this
                calculator. Employment benefits,
                gratuity, severance, tax
                treatment, inflation, visa
                conditions and legal entitlements
                may vary. Verify important
                figures with qualified financial,
                legal, tax or employment
                advisers.
              </p>

            </div>


            {/* =================================================
                CALCULATED RESULT
            ================================================= */}

            {
              result && (

                <div
                  className="
                    rc-summary
                  "

                  aria-live="
                    polite
                  "
                >

                  <h2>
                    Financial Snapshot
                  </h2>


                  {
                    result.alert && (

                      <div
                        className="
                          rc-alert
                        "
                      >
                        ⚠️ {result.alert}
                      </div>

                    )
                  }


                  <div
                    className="
                      rc-runway-grid
                    "
                  >

                    <div>

                      <h5>
                        Financial Runway
                      </h5>


                      <strong>

                        {
                          result
                            .financialRunway >
                          100

                            ? "∞"

                            : `${result.financialRunway.toFixed(
                                1
                              )} Mo`
                        }

                      </strong>

                    </div>


                    <div>

                      <h5>
                        Inflation Drag
                      </h5>


                      <strong>

                        {
                          (
                            result
                              .inflation *
                            100
                          ).toFixed(
                            1
                          )
                        }
                        %

                      </strong>

                    </div>

                  </div>


                  <div
                    className="
                      rc-summary-row
                    "
                  >

                    <span>
                      Total Runway Fund
                    </span>

                    <b>
                      {
                        money(
                          result
                            .totalRunwayFund
                        )
                      }
                    </b>

                  </div>


                  <div
                    className="
                      rc-summary-row
                    "
                  >

                    <span>
                      Net Monthly Burn
                    </span>

                    <b>
                      {
                        money(
                          result
                            .netBurn
                        )
                      }
                    </b>

                  </div>


                  {
                    result
                      .gratuity >
                      0 && (

                      <div
                        className="
                          rc-summary-row
                        "
                      >

                        <span>
                          Estimated Gratuity /
                          Severance
                        </span>


                        <b>
                          {
                            money(
                              result
                                .gratuity
                            )
                          }
                        </b>

                      </div>

                    )
                  }


                  {
                    result
                      .unemploymentBenefit >
                      0 && (

                      <div
                        className="
                          rc-summary-row
                        "
                      >

                        <span>
                          Unemployment Benefit
                        </span>


                        <b>
                          {
                            money(
                              result
                                .unemploymentBenefit
                            )
                          }
                        </b>

                      </div>

                    )
                  }


                  <div
                    className="
                      mt-6
                      rounded-xl
                      border
                      border-[#EB6AB5]/20
                      bg-[#EB6AB5]/5
                      p-4
                    "
                  >

                    <p
                      className="
                        font-semibold
                        text-white
                      "
                    >
                      This result is temporary.
                    </p>


                    <p
                      className="
                        mt-1
                        text-sm
                        text-white/60
                      "
                    >
                      Reignite Spirit does not
                      save this Financial Runway
                      calculation. Save any
                      figures you personally
                      need before leaving this
                      page.
                    </p>

                  </div>


                  <button
                    type="button"

                    onClick={() =>
                      navigate(
                        "/dashboard"
                      )
                    }

                    className="
                      mt-6
                      w-full
                      rounded-full
                      bg-[#EB6AB5]
                      px-6
                      py-3
                      font-bold
                      text-[#0F0A24]
                      hover:bg-[#ff83d1]
                      transition
                    "
                  >
                    Return to Dashboard →
                  </button>

                </div>

              )
            }

          </div>


          {/* =================================================
              RELATED LINKS
          ================================================= */}

          <section
            className="
              max-w-3xl
              mx-auto
              mt-10
              mb-16
              rounded-2xl
              border
              border-white/10
              bg-white/5
              p-6
            "

            aria-labelledby="
              runway-related-resources
            "
          >

            <h2
              id="
                runway-related-resources
              "

              className="
                text-2xl
                font-bold
                text-white
                mb-5
              "
            >
              Related career planning
              resources
            </h2>


            <div
              className="
                grid
                sm:grid-cols-2
                gap-4
              "
            >

              <Link
  to="/financial-runway-calculator-faq"
  className="
    block
    text-left
    rounded-xl
    border
    border-white/10
    p-4
    hover:border-[#EB6AB5]
    transition
  "
>

                <h3
                  className="
                    font-bold
                    text-white
                  "
                >
                  Financial Runway Calculator FAQ
                </h3>


                <p
                  className="
                    text-sm
                    text-slate-400
                    mt-2
                    leading-relaxed
                  "
                >
                  Understand how the calculator
                  treats savings, expenses,
                  debt, inflation, gratuity and
                  severance.
                </p>

              </Link>


              <Link
                to="/snapshot"

                className="
                  block
                  text-left
                  rounded-xl
                  border
                  border-white/10
                  p-4
                  hover:border-[#EB6AB5]
                  transition
                "
              >

                <h3
                  className="
                    font-bold
                    text-white
                  "
                >
                  Snapshot Career Report
                </h3>


                <p
                  className="
                    text-sm
                    text-slate-400
                    mt-2
                    leading-relaxed
                  "
                >
                  Get a focused career-risk
                  and transition assessment.
                </p>

              </Link>


              <Link
                to="/deep-audit"

                className="
                  block
                  text-left
                  rounded-xl
                  border
                  border-white/10
                  p-4
                  hover:border-[#EB6AB5]
                  transition
                "
              >

                <h3
                  className="
                    font-bold
                    text-white
                  "
                >
                  Deep Career Audit
                </h3>


                <p
                  className="
                    text-sm
                    text-slate-400
                    mt-2
                    leading-relaxed
                  "
                >
                  Explore a more detailed
                  career transition roadmap.
                </p>

              </Link>


              <Link
                to="/methodology"

                className="
                  block
                  text-left
                  rounded-xl
                  border
                  border-white/10
                  p-4
                  hover:border-[#EB6AB5]
                  transition
                "
              >

                <h3
                  className="
                    font-bold
                    text-white
                  "
                >
                  Our Methodology
                </h3>


                <p
                  className="
                    text-sm
                    text-slate-400
                    mt-2
                    leading-relaxed
                  "
                >
                  Review how results are
                  produced and understand
                  their limitations.
                </p>

              </Link>

            </div>

          </section>

        </div>


        <AppFooter
          variant="landing"
          logoSrc={logo}
        />

      </main>

    </div>
  );
}